//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import moment from "moment-timezone";
import * as React from "react";
import { withRouter } from "found";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// chrome-event-reschedule-wrapper imports
import { IProps, IState, IContainer } from "./ChromeEventRescheduleWrapper.d";
import {
  chromeEventRescheduleWrapperFragments,
  chromeEventRescheduleWrapperQuery,
} from "./ChromeEventRescheduleWrapper.gql";
import * as s from "./ChromeEventRescheduleWrapper.styles";
// other internals
import { ChromeEventSuggestedTimes } from "@components/chrome-event-suggested-times";
import { RangeAndDurationPicker } from "@components/range-and-duration-picker";
// util
import { EScheduleRange, getMaxMinSlotForRange } from "@util/scheduler.util";
import { getEventTimeForSchedulingSlot } from "@util/event-time.util";
import { PostMessageManager } from "@util/post-message.util";
import { getCurrentOrg } from "@util/org.util";
import { getRenderTimeZone } from "@util/time-zone.util";
// state
import { rescheduleEventParent } from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { IReduxState } from "@reducers/root.reducer";
// material-ui
import { Button } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { withRouteQuery } from "@routing/routes.util";

//////////////////
// COMPONENT
//////////////////
class ChromeEventRescheduleWrapperCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      range: EScheduleRange.NextSeven,
      specifiedDate: null,
      selectedSlot: null,
      saving: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onRangeDurationChange = (
    _minutesDuration: number,
    range: EScheduleRange,
    specifiedDate: Date | null,
  ) => {
    this.setState({ range, specifiedDate });
  };

  private onSlotSelect = (selectedSlot?: ISchema.ISchedulingSlot) => {
    this.setState({ selectedSlot: selectedSlot || null });
  };

  private rescheduleEvent = () => {
    const { viewer, relay } = this.props;
    const { selectedSlot } = this.state;
    const org = getCurrentOrg(viewer)!;

    if (!org || !selectedSlot) {
      return;
    }

    this.setState({ saving: true });
    const externalEventId = atob(this.props.match.params.encodedExternalEventId);
    const eventTime = getEventTimeForSchedulingSlot(selectedSlot);
    const input = {
      eventTime,
      externalEventId,
      orgRelayId: org.id,
    };

    // fire off the mutation
    rescheduleEventParent(
      relay.environment,
      input,
      this.onRescheduleSuccess,
      this.onRescheduleError,
    );
  };

  // success callback after mutation
  private onRescheduleSuccess = () => {
    this.setState({ saving: false });
    this.props.snackBarOpen("Your event was moved.");
    // HACKDAY TODO: this doesnt seem to work :(
    // because this iframe isn't hooked up to post-message manager
    PostMessageManager.sendWebExtensionHovered(true);
  };

  // error callback after mutation
  private onRescheduleError = (_error: any) => {
    this.setState({ saving: false });
    this.props.snackBarOpen("Sorry, an error occurred moving your meeting.");
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    const { viewer, match, relay, classes } = this.props;
    const org = getCurrentOrg(viewer)!;

    // const calendarId = this.props.match.params.encodedCalendarId && this.props.match.params.encodedCalendarId !== 'f'
    //   ? atob(this.props.match.params.encodedCalendarId)
    //   : org.primaryOrgCalendar;

    const externalEventId = atob(match.params.encodedExternalEventId);
    const specifiedMoment = this.state.specifiedDate ? moment(this.state.specifiedDate) : undefined;
    const { rangeMin, rangeMax } = getMaxMinSlotForRange(this.state.range, specifiedMoment);

    return (
      <div className={classes.container}>
        <div className={classes.actionHeader}>
          <RangeAndDurationPicker
            onChange={this.onRangeDurationChange}
            range={this.state.range}
            specifiedDate={this.state.specifiedDate}
            isReschedulerEvent
          />
          {this.state.selectedSlot ? (
            <Button
              variant="text"
              color="primary"
              onClick={() => this.rescheduleEvent()}
              classes={{
                root: classes.rescheduleBtn,
              }}
              disabled={this.state.saving}
            >
              {this.state.saving ? "Saving" : "Reschedule"}
            </Button>
          ) : (
            ""
          )}
        </div>
        <ChromeEventSuggestedTimes
          environment={relay.environment}
          orgId={org.id}
          externalEventId={externalEventId}
          timeZone={getRenderTimeZone()}
          minSlot={rangeMin}
          maxSlot={rangeMax}
          range={this.state.range}
          specifiedDate={this.state.specifiedDate}
          persons={[]}
          onChange={this.onSlotSelect}
          minutesDuration={30} // TODO: get actual meeting length
        />
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
const ChromeEventRescheduleWrapperStyled = withStyles(s.styles)(ChromeEventRescheduleWrapperCmpt);

//////////////////
// WITH ROUTER
//////////////////
const ChromeEventRescheduleWrapperWithRouter = withRouter(ChromeEventRescheduleWrapperStyled);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(ChromeEventRescheduleWrapperWithRouter);

//////////////////
// RELAY
//////////////////
export const ChromeEventRescheduleWrapper = createFragmentContainer<IContainer>(
  reduxContainer,
  chromeEventRescheduleWrapperFragments,
);

export const ChromeEventRescheduleWrapperPage = withRouteQuery(
  ChromeEventRescheduleWrapper,
  chromeEventRescheduleWrapperQuery,
);
